import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Fingerprint, LockKeyhole, Plus } from "lucide-react";
import { useState, type FormEvent } from "react";
import { api, formatApiError } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { EmptyState, ErrorState, LoadingState, StatusBadge } from "../components/AsyncState";
import { Field, PageHeader } from "../components/PageHeader";
import { formatDateTime, formatNumber } from "../lib/format";
import type { Unit } from "./UnitsPage";

type Cycle = {
  id: string;
  cycle_code: string;
  name_ar: string;
  cycle_type: string;
  status: string;
  household_count: number;
  seed_commitment_hash: string;
  result_digest: string;
  locked_at: string;
  scope_name: string;
};

export function CyclesPage() {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState("");
  const [scopeUnitId, setScopeUnitId] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const query = useQuery({ queryKey: ["cycles"], queryFn: async () => (await api<Cycle[]>("/cycles")).data });
  const units = useQuery({
    queryKey: ["units"],
    queryFn: async () => (await api<Unit[]>("/units/tree")).data,
    enabled: createOpen
  });
  const create = useMutation({
    mutationFn: () =>
      api("/cycles/foundational", {
        method: "POST",
        body: { nameAr: name, scopeUnitId, knownRecentHouseholdIds: [] }
      }),
    onSuccess: async () => {
      setNotice("أُنشئت الدورة وأُقفلت ببصمة ثابتة. لا يمكن إعادة ترتيبها.");
      setCreateOpen(false);
      setName("");
      setAcknowledged(false);
      await queryClient.invalidateQueries({ queryKey: ["cycles"] });
    }
  });
  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    create.mutate();
  }
  return (
    <div className="page">
      <PageHeader
        title="دورات الاستحقاق"
        description="كل دورة تأسيسية تُنشأ مرة واحدة وتُقفل ببصمات تحقق ثابتة."
        actions={
          can("distribution.allocate") ? (
            <button className="button" type="button" onClick={() => setCreateOpen(true)}>
              <Plus aria-hidden="true" /> دورة تأسيسية
            </button>
          ) : undefined
        }
      />
      {notice ? (
        <div className="success-panel" role="status">
          <LockKeyhole aria-hidden="true" />
          <p>{notice}</p>
        </div>
      ) : null}
      {createOpen ? (
        <section className="content-card management-form" aria-labelledby="cycle-form-title">
          <h2 id="cycle-form-title">إنشاء دورة تأسيسية مقفلة</h2>
          <p>
            هذه العملية تولّد ترتيبًا حتميًا موثقًا داخل الدورة، وتُقفل النتيجة فورًا. لا تُكررها للوصول إلى
            نتيجة مرغوبة.
          </p>
          <form className="form-grid" onSubmit={submit}>
            <Field label="اسم الدورة" htmlFor="cycle-name">
              <input
                id="cycle-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                minLength={3}
                required
              />
            </Field>
            <Field label="النطاق الجغرافي" htmlFor="cycle-scope">
              <select
                id="cycle-scope"
                value={scopeUnitId}
                onChange={(e) => setScopeUnitId(e.target.value)}
                required
              >
                <option value="">اختر النطاق</option>
                {(units.data ?? [])
                  .filter((unit) => unit.status === "active")
                  .map((unit) => (
                    <option key={unit.id} value={unit.id}>
                      {unit.name_ar}
                    </option>
                  ))}
              </select>
            </Field>
            <label className="check-option form-span">
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
                required
              />
              <span>
                <strong>أفهم أن الدورة ستُقفل ولن تُعاد القرعة لتحسين النتيجة.</strong>
              </span>
            </label>
            {create.isError ? (
              <div className="inline-alert form-span" role="alert">
                {formatApiError(create.error)}
              </div>
            ) : null}
            <div className="dialog-actions form-span">
              <button className="button button--secondary" type="button" onClick={() => setCreateOpen(false)}>
                إلغاء
              </button>
              <button className="button" type="submit" disabled={create.isPending || !acknowledged}>
                {create.isPending ? "جارٍ بناء الترتيب…" : "إنشاء وقفل الدورة"}
              </button>
            </div>
          </form>
        </section>
      ) : null}
      {query.isPending ? (
        <LoadingState />
      ) : query.isError ? (
        <ErrorState error={query.error} retry={() => void query.refetch()} />
      ) : query.data.length === 0 ? (
        <EmptyState title="لا توجد دورات" description="أنشئ الدورة التأسيسية بعد اكتمال سجل الأسر." />
      ) : (
        <section className="card-list">
          {query.data.map((cycle) => (
            <article className="cycle-card" key={cycle.id}>
              <div className="row-between">
                <div className="cycle-title">
                  <div className="metric-icon">
                    <LockKeyhole aria-hidden="true" />
                  </div>
                  <div>
                    <span>{cycle.cycle_code}</span>
                    <h2>{cycle.name_ar}</h2>
                    <p>
                      {cycle.scope_name} · {formatNumber(cycle.household_count)} أسرة
                    </p>
                  </div>
                </div>
                <StatusBadge status={cycle.status} />
              </div>
              <dl className="digest-grid">
                <div>
                  <dt>
                    <Fingerprint aria-hidden="true" />
                    بصمة الالتزام
                  </dt>
                  <dd dir="ltr">{cycle.seed_commitment_hash.slice(0, 24)}…</dd>
                </div>
                <div>
                  <dt>
                    <Fingerprint aria-hidden="true" />
                    بصمة النتيجة
                  </dt>
                  <dd dir="ltr">{cycle.result_digest.slice(0, 24)}…</dd>
                </div>
                <div>
                  <dt>تاريخ القفل</dt>
                  <dd>{formatDateTime(cycle.locked_at)}</dd>
                </div>
              </dl>
            </article>
          ))}
        </section>
      )}
    </div>
  );
}
